import type { InjectionKey } from 'vue';

/** Panel size in CSS pixels (border box). */
export type ModalSize = { width: number; height: number };

/** Bounds of a resize: natural size, visible maximum and optionally the room in the drag direction. */
export type ModalResizeLimits = {
  min: ModalSize;
  max: ModalSize;
  room?: { width: number; height: number };
};

/** Edge or corner a resize handle sits on. */
export type ModalResizeEdge = 'n' | 'e' | 's' | 'w' | 'se';

/** What is remembered: the normal (not maximized) size and whether it was maximized. */
export type StoredModalSize = ModalSize & { maximized: boolean };

function clampAxis(value: number, min: number, max: number): number {
  // A window smaller than the natural size wins over the minimum: the panel
  // must stay inside the visible area.
  return Math.max(Math.min(min, max), Math.min(value, max));
}

/** Clamp a size between the natural (minimum) and the visible (maximum) size. */
export function clampModalSize(size: ModalSize, min: ModalSize, max: ModalSize): ModalSize {
  return {
    width: clampAxis(size.width, min.width, max.width),
    height: clampAxis(size.height, min.height, max.height),
  };
}

/** Largest panel size: the viewport minus `marginPx` on every side. */
export function maxModalSize(viewport: ModalSize, marginPx: number): ModalSize {
  return {
    width: Math.max(0, viewport.width - marginPx * 2),
    height: Math.max(0, viewport.height - marginPx * 2),
  };
}

function clampAxisShift(
  shift: number,
  drag: number,
  size: number,
  viewport: number,
  marginPx: number
): number {
  // The panel is centred, so its start edge sits at (viewport - size) / 2 before
  // any shift or drag; the allowed total movement keeps both edges inside the margin.
  const free = (viewport - size) / 2;
  const low = marginPx - free;
  const high = viewport - marginPx - size - free;
  // Wider than the visible area (a window too small even for the natural size): centre.
  const total = low > high ? (low + high) / 2 : Math.min(Math.max(shift + drag, low), high);
  return total - drag;
}

/**
 * Shift that keeps the whole panel inside the visible area (viewport minus
 * `marginPx`). The panel's position is its centred layout position plus
 * NxModal's header drag offset plus our `shift`; only the shift is adjusted, so
 * the drag offset stays NxModal's own. Call it after every size change that
 * does not come from a drag (window resize, restore, open).
 */
export function clampModalShift(
  shift: { x: number; y: number },
  dragOffset: { x: number; y: number },
  size: ModalSize,
  area: { viewport: ModalSize; marginPx: number }
): { x: number; y: number } {
  const { viewport, marginPx } = area;
  return {
    x: clampAxisShift(shift.x, dragOffset.x, size.width, viewport.width, marginPx),
    y: clampAxisShift(shift.y, dragOffset.y, size.height, viewport.height, marginPx),
  };
}

/**
 * How far the panel may extend in the drag direction, per axis, when the edge
 * opposite to `edge` stays put: from that fixed edge to the visible area
 * (viewport minus `marginPx`). An axis the edge does not touch is unbounded.
 * `rect` is the panel's current box in viewport pixels.
 */
export function modalResizeRoom(
  edge: ModalResizeEdge,
  rect: { left: number; top: number; right: number; bottom: number },
  viewport: ModalSize,
  marginPx: number
): { width: number; height: number } {
  const right = viewport.width - marginPx;
  const bottom = viewport.height - marginPx;
  return {
    width:
      edge === 'e' || edge === 'se'
        ? right - rect.left
        : edge === 'w'
          ? rect.right - marginPx
          : Infinity,
    height:
      edge === 's' || edge === 'se'
        ? bottom - rect.top
        : edge === 'n'
          ? rect.bottom - marginPx
          : Infinity,
  };
}

/**
 * Size after dragging `edge` by `delta` pixels from `start`, plus the shift the
 * panel needs so the OPPOSITE edge stays where it was.
 *
 * `room` (see `modalResizeRoom`) stops the dragged edge at the visible area
 * instead of letting the fixed opposite edge push the panel out of the window;
 * it never squeezes the panel below the natural size `min`.
 *
 * The panel is centred in its host, so growing it by `d` moves each edge by
 * `d / 2`. Shifting the panel by `d / 2` toward the dragged edge (away from it
 * for the left and top edges) puts the opposite edge back. `shift` is relative
 * to the shift in force when the drag started.
 */
export function resizeModalSize(
  edge: ModalResizeEdge,
  start: ModalSize,
  delta: { x: number; y: number },
  limits: ModalResizeLimits
): { size: ModalSize; shift: { x: number; y: number } } {
  let width = start.width;
  let height = start.height;
  if (edge === 'e' || edge === 'se') width = start.width + delta.x;
  if (edge === 'w') width = start.width - delta.x;
  if (edge === 's' || edge === 'se') height = start.height + delta.y;
  if (edge === 'n') height = start.height - delta.y;

  const { min, max, room = { width: Infinity, height: Infinity } } = limits;
  // The room never squeezes below the natural size, but a window smaller than
  // that still wins (as in clampModalSize).
  const reach = {
    width: Math.min(max.width, Math.max(min.width, room.width)),
    height: Math.min(max.height, Math.max(min.height, room.height)),
  };
  const size = clampModalSize({ width, height }, min, reach);
  const grewX = (size.width - start.width) / 2;
  const grewY = (size.height - start.height) / 2;
  return {
    size,
    shift: {
      x: edge === 'w' ? -grewX : edge === 'e' || edge === 'se' ? grewX : 0,
      y: edge === 'n' ? -grewY : edge === 's' || edge === 'se' ? grewY : 0,
    },
  };
}

/** A remembered size (the pane state); anything missing, corrupt, non-finite or non-positive is `null`. */
export function storedModalSize(value: unknown): StoredModalSize | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const { width, height, maximized } = value as {
    width?: unknown;
    height?: unknown;
    maximized?: unknown;
  };
  if (typeof width !== 'number' || typeof height !== 'number') return null;
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return null;
  }
  return { width, height, maximized: maximized === true };
}

/**
 * The modal's resize path, handed to a grip inside the body (`NxModalResizeGrip`): the same
 * start / move / end an edge handle uses, so a field grip sizes the MODAL — top-left anchored,
 * clamped to the window and the natural size, remembered — instead of the field alone.
 */
export type ModalResizeDriver = {
  /** Whether the modal is resizable at all; a grip shows only then. */
  enabled: () => boolean;
  /** Whether the dialog is open; a grip starts every open at its field's own height. */
  isOpen: () => boolean;
  /** How much taller than its natural size the panel is now (resized, remembered, maximized). */
  growth: () => number;
  /** Starts a resize from `edge`; the panel size it starts from, or null when none starts. */
  start: (e: PointerEvent, edge: ModalResizeEdge) => ModalSize | null;
  /** The panel size after this move, or null outside a resize. */
  move: (e: PointerEvent) => ModalSize | null;
  end: (e: PointerEvent) => void;
};

export const MODAL_RESIZE_DRIVER: InjectionKey<ModalResizeDriver> =
  Symbol('nx-modal-resize-driver');

/**
 * The height of a grip's field box: its own height plus all the height the modal has over its
 * natural size (`growth`) — the field fills the modal (owner, I-2563). While the grip drags, the
 * growth changes by exactly what the modal's height does, so the grip stays under the pointer and
 * stops where the modal stops; a remembered or maximized size gives the field its share on open.
 * `null` while the modal is at its natural height: nothing to set.
 */
export function gripFieldHeight(own: number, growth: number): number | null {
  return growth > 0 ? own + growth : null;
}
